import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { YachtForm } from "./yacht.schema";
import { Input } from "@/src/components/ui/input";
import { ImagesPreview, UploadInput } from "@/src/components/ui/upload";
import { Combobox } from "@/src/components/ui/combobox";
import { fetch } from "@/src/lib/fetch";
import { Model } from "./model";
import { useFormContext } from "react-hook-form";
import { InputNumber } from "@/src/components/ui/input-number";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { Checkbox } from "@/src/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";
import { Textarea } from "@/src/components/ui/textarea";
import { Card } from "@/src/components/wrappers/Card";
import { useFactoryDialog } from "@/src/lib/useFactoryDialog";
import { AddModelDialog } from "./AddModelDialog";
import { AddLabelDialog } from "./AddLabelDialog";
import { YachtLabel } from "./model";
import { ComboboxMultiYachtLabels } from "@/src/components/ui/combobox-multi-yacht-labels";
import { useOperatorLabelsData } from "@/src/hooks/useOperatorLabelsData";
import { YachtDetails } from "../model";
import { useLoaderData } from "react-router-dom";
import { ScoreSelect } from "@/src/components/ui/score-select";
import { MetaDetails } from "./MetaDetails";

export function BasicDetails() {
  const form = useFormContext<YachtForm>();
  const yacht = useLoaderData() as YachtDetails;

  const { operatorsLabelsData } = useOperatorLabelsData(yacht?.id);

  const [addModelDialogProps, addModelDialogTrigger] = useFactoryDialog<Model>();
  const [AddLabelDialogProps, addLabelDialogTrigger] = useFactoryDialog<YachtLabel>();

  return (
    <Card>
      <div className="grid gap-4 max-w-[1016px] grid-cols-[repeat(auto-fit,minmax(150px,328px))]">
        <h3 className="text-lg font-semibold col-span-full">Basic Details</h3>
        <h5 className="text-sm col-span-full">Add yacht photos and other required details.</h5>
        <FormField<YachtForm, "basicDetails.photos">
          name="basicDetails.photos"
          render={({ field: { onChange, value } }) => (
            <FormItem className="col-span-full" required>
              <FormLabel>Images</FormLabel>
              <FormControl>
                <UploadInput
                  multiple
                  accept="image/png, image/jpg"
                  ownerType="yacht"
                  value={value}
                  onChange={onChange}
                  renderFiles={(p) => <ImagesPreview {...p} />}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.name">
          name="basicDetails.name"
          render={({ field }) => (
            <FormItem required>
              <FormLabel>Yacht name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.model">
          name="basicDetails.model"
          render={({ field }) => (
            <FormItem className="flex flex-col justify-between items-stretch" required>
              <FormLabel className="leading-6">Yacht model</FormLabel>
              <FormControl>
                <Combobox<Model>
                  onAddOption={addModelDialogTrigger}
                  className="w-full"
                  loadOptions={(q) =>
                    fetch<{ data: Model[] }>(`/api/models/models?pageSize=5&filter=${q}`).then((r) => r.data)
                  }
                  renderOption={(v) => v.name}
                  value={field.value as Model}
                  onChange={(v) => {
                    field.onChange(v && { id: v.id, name: v.name });
                    form.setValue("basicDetails.manufacturer", v?.manufacturer ?? "");
                  }}
                />
              </FormControl>
              <AddModelDialog {...addModelDialogProps} />
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.manufacturer">
          name="basicDetails.manufacturer"
          render={({ field }) => (
            <FormItem required>
              <FormLabel>Yacht manufacturer</FormLabel>
              <FormControl>
                <Input {...field} readOnly placeholder="Select model" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.productionYear">
          name="basicDetails.productionYear"
          render={({ field }) => (
            <FormItem required>
              <FormLabel>Production year</FormLabel>
              <FormControl>
                <InputNumber {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.type">
          name="basicDetails.type"
          render={({ field: { onChange, value } }) => (
            <FormItem required>
              <FormLabel>Yacht type</FormLabel>
              <FormControl>
                <Select onValueChange={onChange} defaultValue={value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Sailing yacht">Sailing yacht</SelectItem>
                    <SelectItem value="Catamaran">Catamaran</SelectItem>
                    <SelectItem value="Do not display">Do not display</SelectItem>
                    <SelectItem value="Gulet">Gulet</SelectItem>
                    <SelectItem value="Jet ski">Jet ski</SelectItem>
                    <SelectItem value="Power catamaran">Power catamaran</SelectItem>
                    <SelectItem value="R.I.B / Speed boat">R.I.B / Speed boat</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="Motor yacht">Motor yacht</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.operator">
          name="basicDetails.operator"
          render={({ field }) => (
            <FormItem className="flex flex-col justify-between items-stretch" required>
              <FormLabel>Operator</FormLabel>
              <FormControl>
                <Combobox
                  className="w-full"
                  loadOptions={(query) =>
                    fetch<{ data: { name: string; id: string }[] }>(
                      `/api/operators/operators?pageSize=5&filter=${query}`,
                    ).then((r) => r.data)
                  }
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v && { id: v.id, name: v.name });
                  }}
                  renderOption={(r) => r.name}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.startDay">
          name="basicDetails.startDay"
          render={({ field: { onChange, value } }) => (
            <FormItem required>
              <FormLabel>Charter start day</FormLabel>
              <FormControl>
                <Select onValueChange={onChange} defaultValue={value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="1">Monday</SelectItem>
                    <SelectItem value="2">Tuesday</SelectItem>
                    <SelectItem value="3">Wednesday</SelectItem>
                    <SelectItem value="4">Thursday</SelectItem>
                    <SelectItem value="5">Friday</SelectItem>
                    <SelectItem value="6">Saturday</SelectItem>
                    <SelectItem value="0">Sunday</SelectItem>
                    <SelectItem value="any day">Any day</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.minimumCharterDuration">
          name="basicDetails.minimumCharterDuration"
          render={({ field }) => (
            <FormItem required>
              <FormLabel>Minimum charter duration (days)</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.provider">
          name="basicDetails.provider"
          render={({ field: { onChange, value } }) => (
            <FormItem>
              <FormLabel>Yacht Provider</FormLabel>
              <FormControl>
                <Select onValueChange={onChange} defaultValue={value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="NS">NS</SelectItem>
                    <SelectItem value="CYA">CYA</SelectItem>
                    <SelectItem value="BM">BM</SelectItem>
                    <SelectItem value="Helm">Helm</SelectItem>
                    <SelectItem value="Yachtfolio">Yachtfolio</SelectItem>
                    <SelectItem value="Ankor">Ankor</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.providerId">
          name="basicDetails.providerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Provider Yacht ID</FormLabel>
              <FormControl>
                <Input {...field} value={!field.value ? "" : field.value} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.ratingHelm">
          name="basicDetails.ratingHelm"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Helm rating</FormLabel>
              <FormControl>
                <ScoreSelect min={1} max={10} {...field} value={!field.value ? 0 : field.value} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.ratingAnchor">
          name="basicDetails.ratingAnchor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Anchor rating</FormLabel>
              <FormControl>
                <ScoreSelect min={1} max={10} {...field} value={!field.value ? 0 : field.value} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField<YachtForm, "basicDetails.charterTypes">
          name="basicDetails.charterTypes"
          render={({ field }) => (
            <div className="col-span-full">
              <FormItem required>
                <FormLabel>Select charter types</FormLabel>
              </FormItem>
              <FormItem className="flex gap-2 items-center space-y-0 py-[6px] px-4">
                <Checkbox
                  checked={field.value.bareboat}
                  onCheckedChange={() => {
                    field.onChange({ ...field.value, bareboat: !field.value.bareboat });
                  }}
                />
                <FormLabel className="font-light text-base">Bareboat</FormLabel>
              </FormItem>
              <FormItem className="flex gap-2 items-center space-y-0 py-[6px] px-4">
                <Checkbox
                  checked={field.value.skippered}
                  onCheckedChange={() => {
                    field.onChange({ ...field.value, skippered: !field.value.skippered });
                  }}
                />
                <FormLabel className="font-light text-base">Skippered</FormLabel>
              </FormItem>
              <FormItem className="flex gap-2 items-center space-y-0 py-[6px] px-4">
                <Checkbox
                  checked={field.value.crewed}
                  onCheckedChange={() => {
                    field.onChange({ ...field.value, crewed: !field.value.crewed });
                  }}
                />
                <FormLabel className="font-light text-base">Crewed</FormLabel>
              </FormItem>
            </div>
          )}
        />

        <div className="col-span-full grid grid-cols-2 gap-6 items-end">
          <div>
            <FormField<YachtForm, `basicDetails.labels`>
              name="basicDetails.labels"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Yacht Labels</FormLabel>
                  <FormControl>
                    <ComboboxMultiYachtLabels<YachtLabel>
                      className="w-full"
                      onAddOption={addLabelDialogTrigger}
                      loadOptions={(query) => {
                        const data = fetch<{ data: YachtLabel[]; success: boolean }>(
                          `/api/yachts/labels${query ? `?query=${query}` : ""}`,
                        ).then((r: any) =>
                          r.data?.map((y: YachtLabel) => {
                            return {
                              value: y.value,
                              label: y.value,
                            };
                          }),
                        );
                        return data;
                      }}
                      placeholder="Search for label..."
                      value={(field.value ?? [])?.map((l) => l.value)}
                      onChange={(selectedValues: string[] | unknown) => {
                        if (!Array.isArray(selectedValues)) return;

                        const fullObjects = selectedValues.map((val) => {
                          const existing = (field.value ?? []).find((v) => v.value === val);
                          return existing ?? { value: val, id: crypto.randomUUID() };
                        });
                        field.onChange(fullObjects);
                      }}
                    />
                  </FormControl>
                  <AddLabelDialog {...AddLabelDialogProps} />
                </FormItem>
              )}
            />
          </div>
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-gray-500 mb-2">Operator Labels:</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-500">
              {operatorsLabelsData?.map((label) => (
                <li key={label.value} className="text-sm">
                  {label.value}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="col-span-full grid grid-cols-3 gap-6">
          <FormField<YachtForm, "basicDetails.offerCreatorVisibility">
            name="basicDetails.offerCreatorVisibility"
            render={({ field }) => (
              <div className="min-w-32">
                <FormItem required>
                  <FormLabel>Yacht visibility - Offer creator</FormLabel>
                  <FormControl>
                    <RadioGroup defaultValue={field.value} onValueChange={field.onChange}>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="auto" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Auto</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="visible" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Visible</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="hidden" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Hidden</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              </div>
            )}
          />
          <FormField<YachtForm, "basicDetails.helmVisibility">
            name="basicDetails.helmVisibility"
            render={({ field }) => (
              <div className="min-w-32">
                <FormItem required>
                  <FormLabel>Yacht visibility - HELM Website</FormLabel>
                  <FormControl>
                    <RadioGroup defaultValue={field.value} onValueChange={field.onChange}>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="auto" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Auto</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="visible" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Visible</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="hidden" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Hidden</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              </div>
            )}
          />
          <FormField<YachtForm, "basicDetails.anchorVisibility">
            name="basicDetails.anchorVisibility"
            render={({ field }) => (
              <div className="min-w-32">
                <FormItem required>
                  <FormLabel>Yacht visibility - Anchor Website</FormLabel>
                  <FormControl>
                    <RadioGroup defaultValue={field.value} onValueChange={field.onChange}>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="auto" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Auto</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="visible" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Visible</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="hidden" />
                        </FormControl>
                        <FormLabel className="font-light text-base">Hidden</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              </div>
            )}
          />
        </div>
        <FormField<YachtForm, "basicDetails.description">
          name="basicDetails.description"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea className="resize-none" maxLength={1000} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<YachtForm, "basicDetails.notes">
          name="basicDetails.notes"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>Broker notes</FormLabel>
              <FormControl>
                <Textarea className="resize-none" maxLength={1000} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <MetaDetails />
      </div>
    </Card>
  );
}
